import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Switch, 
  FormControlLabel, Typography, Box, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Paper, TextField, Autocomplete, IconButton, Divider, Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const WarehouseAutocomplete = ({ initialValue, onChange, userId, supabase }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!open || !userId || !supabase) return;
    
    setLoading(true);
    const doSearch = async () => {
      try {
        const t = search.trim();
        let query = supabase.from('invoice_erp_locations').select('codigo, descripcion').eq('tenant_id', userId).limit(50);
        if (t) {
          query = query.or('codigo.ilike.%' + t + '%,descripcion.ilike.%' + t + '%');
        }
        const { data, error } = await query;
        if (!error && data) {
          setOptions(data);
        }
      } catch (err) {
        console.error('Error searching locations:', err);
      } finally {
        setLoading(false);
      }
    };
    
    const timeoutId = setTimeout(doSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [search, open, userId, supabase]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      isOptionEqualToValue={(option, value) => option.codigo === value.codigo}
      getOptionLabel={(option) => typeof option === 'string' ? option : option.codigo + ' - ' + option.descripcion}
      options={options}
      loading={loading}
      inputValue={search}
      onInputChange={(event, newInputValue, reason) => {
        setSearch(newInputValue);
        if (reason === 'clear') {
          onChange(null);
        }
      }}
      value={options.find(o => o.codigo === initialValue) || (initialValue ? { codigo: initialValue, descripcion: '' } : null)}
      onChange={(event, newValue) => {
        const newCode = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : null;
        onChange(newCode);
        if (newValue && typeof newValue !== 'string') {
          setSearch(newValue.codigo + ' - ' + newValue.descripcion);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Buscar bodega..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <span style={{ fontSize: '0.8rem' }}>...</span> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};
export default function GeneralSettingsModal({ open, onClose, authState, authStore, supabase }) {
  const [advanceConfig, setAdvanceConfig] = useState(null);
  const [advanceSaving, setAdvanceSaving] = useState(false);
  const [advanceError, setAdvanceError] = useState('');
  useEffect(() => {
    let active = true;
    setAdvanceConfig(null);
    setAdvanceError('');
    if (open && authState?.user?.id) {
      supabase.from('invoice_config').select('enable_advances_sync, auto_apply_advances')
        .eq('user_id', authState.user.id).maybeSingle().then(({ data, error }) => {
          if (!active) return;
          if (error) setAdvanceError('No se pudo cargar la configuración de anticipos: ' + error.message);
          else setAdvanceConfig(data);
        });
    }
    return () => { active = false; };
  }, [open, authState?.user?.id, supabase]);

  const handleAutomaticAdvances = async (event) => {
    const value = event.target.checked;
    setAdvanceSaving(true);
    setAdvanceError('');
    try {
      const { data, error } = await supabase.from('invoice_config')
        .update({ auto_apply_advances: value }).eq('user_id', authState.user.id)
        .select('enable_advances_sync, auto_apply_advances').single();
      if (error) throw error;
      setAdvanceConfig(data);
    } catch (error) {
      setAdvanceError('No se guardó el cambio: ' + error.message);
    } finally { setAdvanceSaving(false); }
  };
  const [enableCustomDocTypes, setEnableCustomDocTypes] = useState(false);
  const [enableInvoiceItemEditing, setEnableInvoiceItemEditing] = useState(false);
  const [itemEditingSaving, setItemEditingSaving] = useState(false);
  const [itemEditingError, setItemEditingError] = useState('');
  const [exactProviderItemCode, setExactProviderItemCode] = useState(false);
  const [enableInventoryProration, setEnableInventoryProration] = useState(false);
  const [freightAccount, setFreightAccount] = useState('');
  const [enablePurchaseOrders, setEnablePurchaseOrders] = useState(false);
  const [poPriorityMode, setPoPriorityMode] = useState(false);
  const [showUploadInvoiceTypeSelectors, setShowUploadInvoiceTypeSelectors] = useState(false);
  const [docTypes, setDocTypes] = useState([]);
  const [docTypeWarehouses, setDocTypeWarehouses] = useState([]);
  
  useEffect(() => {
    if (open && authState?.user) {
      setItemEditingError('');
      setEnableCustomDocTypes(authState.user.enable_custom_doc_types || false);
      setExactProviderItemCode(authState.user.exact_provider_item_code || false);
      setEnableInventoryProration(authState.user.enable_inventory_proration || false);
      setFreightAccount(authState.user.freight_account || '');
      setEnablePurchaseOrders(authState.user.enable_purchase_orders || false);
      setPoPriorityMode(authState.user.po_priority_mode || false);
      setShowUploadInvoiceTypeSelectors(authState.user.show_upload_invoice_type_selectors || false);
      setDocTypes(authState.tenantDocTypes || []);
      setDocTypeWarehouses(authState.docTypeWarehouses || []);
      if (authStore?.getState()?.fetchDocTypeWarehouses) {
        authStore.getState().fetchDocTypeWarehouses();
      }
    }
  }, [open, authState?.user, authStore]);

  useEffect(() => {
    if (!authStore) return;
    const unsubscribe = authStore.subscribe((state) => {
      setDocTypeWarehouses(state.docTypeWarehouses || []);
    });
    setDocTypeWarehouses(authStore.getState().docTypeWarehouses || []);
    return () => unsubscribe();
  }, [authStore]);

  useEffect(() => {
    if (!open || !authState?.user?.id || !supabase) return;
    let active = true;
    setEnableInvoiceItemEditing(false);
    setItemEditingSaving(true);
    supabase.from('invoice_users').select('enable_invoice_item_editing').eq('id', authState.user.id).single()
      .then(({ data, error }) => {
        if (!active) return;
        if (error) setItemEditingError('No se pudo consultar la opción. Cierra y vuelve a abrir Opciones generales.');
        else setEnableInvoiceItemEditing(data?.enable_invoice_item_editing === true);
      }).catch(() => { if (active) setItemEditingError('No se pudo consultar la opción. Cierra y vuelve a abrir Opciones generales.'); })
      .finally(() => { if (active) setItemEditingSaving(false); });
    return () => { active = false; };
  }, [open, authState?.user?.id, supabase]);

  const handleToggleItemEditing = async (event) => {
    const value = event.target.checked;
    setItemEditingSaving(true);
    setItemEditingError('');
    try {
      const { data, error } = await supabase.from('invoice_users')
        .update({ enable_invoice_item_editing: value }).eq('id', authState.user.id)
        .select('enable_invoice_item_editing').single();
      if (error || !data || data.enable_invoice_item_editing !== value) throw error || new Error('Setting not saved');
      setEnableInvoiceItemEditing(value);
      authStore.getState().updateUser?.({ enable_invoice_item_editing: value });
    } catch {
      setItemEditingError('No se pudo guardar la opción. Inténtalo de nuevo.');
    } finally {
      setItemEditingSaving(false);
    }
  };

  const handleToggleEnable = async (e) => {
    const val = e.target.checked;
    setEnableCustomDocTypes(val);
    try {
      const { error } = await supabase
        .from('invoice_users')
        .update({ enable_custom_doc_types: val })
        .eq('id', authState.user.id);
      
      if (!error) {
        if (authStore.getState().updateUser) {
          authStore.getState().updateUser({ enable_custom_doc_types: val });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleUploadInvoiceTypeSelectors = async (e) => {
    const val = e.target.checked;
    setShowUploadInvoiceTypeSelectors(val);

    try {
      const { error } = await supabase
        .from('invoice_users')
        .update({ show_upload_invoice_type_selectors: val })
        .eq('id', authState.user.id);

      if (error) throw error;

      if (authStore.getState().updateUser) {
        authStore.getState().updateUser({ show_upload_invoice_type_selectors: val });
      }
    } catch (err) {
      console.error('Error updating upload invoice type selector setting:', err);
      setShowUploadInvoiceTypeSelectors(!val);
    }
  };

  const handleToggleExactItemCode = async (e) => {
    const val = e.target.checked;
    setExactProviderItemCode(val);
    try {
      const { error } = await supabase
        .from('invoice_users')
        .update({ exact_provider_item_code: val })
        .eq('id', authState.user.id);
      
      if (!error) {
        if (authStore.getState().updateUser) {
          authStore.getState().updateUser({ exact_provider_item_code: val });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleInventoryProration = async (e) => {
    const val = e.target.checked;
    setEnableInventoryProration(val);
    try {
      const { error } = await supabase
        .from('invoice_users')
        .update({ enable_inventory_proration: val })
        .eq('id', authState.user.id);
      
      if (!error) {
        if (authStore.getState().updateUser) {
          authStore.getState().updateUser({ enable_inventory_proration: val });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleFreightAccountChange = async (e) => {
    const val = e.target.value;
    setFreightAccount(val);
    try {
      const { error } = await supabase
        .from('invoice_users')
        .update({ freight_account: val })
        .eq('id', authState.user.id);
      
      if (!error) {
        if (authStore.getState().updateUser) {
          authStore.getState().updateUser({ freight_account: val });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleTogglePurchaseOrders = async (e) => {
    const val = e.target.checked;
    setEnablePurchaseOrders(val);
    try {
      const { error } = await supabase
        .from('invoice_users')
        .update({ enable_purchase_orders: val })
        .eq('id', authState.user.id);
      
      if (!error) {
        if (authStore.getState().updateUser) {
          authStore.getState().updateUser({ enable_purchase_orders: val });
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleTogglePoPriorityMode = async (e) => {
    const val = e.target.checked;
    setPoPriorityMode(val);
    try {
      // Si se activa po_priority_mode, asegurar que enable_purchase_orders también esté activo
      const updates = { po_priority_mode: val };
      if (val && !enablePurchaseOrders) {
        updates.enable_purchase_orders = true;
        setEnablePurchaseOrders(true);
      }
      const { error } = await supabase
        .from('invoice_users')
        .update(updates)
        .eq('id', authState.user.id);
      
      if (!error) {
        if (authStore.getState().updateUser) {
          authStore.getState().updateUser(updates);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleWarehouseChange = async (docTypeCode, newWarehouseCode) => {
    try {
      if (!newWarehouseCode) {
        const { error } = await supabase
          .from('invoice_custom_doc_type_warehouses')
          .delete()
          .eq('tenant_user_id', authState.user.id)
          .eq('custom_doc_type', docTypeCode);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('invoice_custom_doc_type_warehouses')
          .upsert({
            tenant_user_id: authState.user.id,
            custom_doc_type: docTypeCode,
            warehouse_code: newWarehouseCode,
            updated_at: new Date().toISOString()
          }, { onConflict: 'tenant_user_id, custom_doc_type' });
        if (error) throw error;
      }
      
      if (authStore?.getState()?.fetchDocTypeWarehouses) {
        await authStore.getState().fetchDocTypeWarehouses();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const getWarehouseForDocType = (code) => {
    const w = docTypeWarehouses.find(d => d.custom_doc_type === code);
    return w ? w.warehouse_code : null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configuraciones Generales</DialogTitle>
      <DialogContent dividers>
        {advanceError && <Typography color="error" role="alert">{advanceError}</Typography>}
        {advanceConfig?.enable_advances_sync && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6">Anticipos de proveedores</Typography>
            <FormControlLabel label="Aplicar anticipos automáticamente"
              control={<Switch checked={Boolean(advanceConfig.auto_apply_advances)}
                disabled={advanceSaving} onChange={handleAutomaticAdvances} />} />
            <Typography variant="body2" color="textSecondary">
              Si está desactivado, los anticipos solo se cruzan al pulsar Cruzar.
              Quitar un anticipo impide que vuelva a aplicarse automáticamente en esa factura.
              Los cruces existentes no se eliminan al cambiar esta opción.
            </Typography>
          </Box>
        )}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Edición de líneas de facturas</Typography>
          <FormControlLabel
            control={<Switch checked={enableInvoiceItemEditing} disabled={itemEditingSaving} onChange={handleToggleItemEditing} />}
            label="Permitir editar líneas en Ver Detalle"
          />
          <Typography variant="body2" color="textSecondary">
            Habilita la edición de las líneas existentes solo para este cliente. Desactivada por defecto.
          </Typography>
          {itemEditingError && <Alert severity="error" sx={{ mt: 1 }}>{itemEditingError}</Alert>}
        </Box>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Configuración de carga de archivos</Typography>
          <FormControlLabel
            control={(
              <Switch
                checked={showUploadInvoiceTypeSelectors}
                onChange={handleToggleUploadInvoiceTypeSelectors}
                color="primary"
              />
            )}
            label="Mostrar selectores de tipo al cargar archivos"
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
            Muestra el selector general y los selectores por archivo durante la carga. Los selectores de las tarjetas siempre permanecen visibles.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Configuración de Tipos de Documento ERP</Typography>
          <FormControlLabel
            control={<Switch checked={enableCustomDocTypes} onChange={handleToggleEnable} color="primary" />}
            label="Habilitar asociación de documentos a bodegas"
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
            Si se activa, el sistema permitirá asociar un tipo de documento a una bodega específica.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Configuración de Facturas de Inventario</Typography>
          <FormControlLabel
            control={<Switch checked={enableInventoryProration} onChange={handleToggleInventoryProration} color="primary" />}
            label="Activar prorrateo para facturas de inventario"
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
            Si se activa, el sistema permitirá distribuir un valor adicional entre los ítems de las facturas tipo inventario.
          </Typography>
          {enableInventoryProration && (
            <TextField
              fullWidth
              size="small"
              label="Cuenta contable de fletes (ACCT)"
              value={freightAccount}
              onChange={handleFreightAccountChange}
              sx={{ ml: 4, mt: 1, maxWidth: 300 }}
              placeholder="Ej: 52355001"
            />
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Reglas de Conciliación de Ítems</Typography>
          <FormControlLabel
            control={<Switch checked={exactProviderItemCode} onChange={handleToggleExactItemCode} color="primary" />}
            label="Código de item exacto en proveedor"
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
            Si se activa, se aplicará el comportamiento de buscar el código de ítem exacto en el proveedor.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>Manejo de Órdenes de Compra</Typography>
          <FormControlLabel
            control={<Switch checked={enablePurchaseOrders} onChange={handleTogglePurchaseOrders} color="primary" />}
            label="Habilitar Manejo de Órdenes de Compra"
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
            Si se activa, el sistema permitirá cruzar las facturas con las órdenes de compra pendientes del proveedor de manera inteligente.
          </Typography>
          <FormControlLabel
            control={<Switch checked={poPriorityMode} onChange={handleTogglePoPriorityMode} color="primary" />}
            label="Priorizar valores de Órdenes de Compra"
          />
          <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mb: 2 }}>
            Si se activa, las cantidades y valores se tomarán de las OCs del proveedor en lugar de los datos de la factura. La búsqueda de productos se limitará a los ítems de las OCs activas.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        {enableCustomDocTypes && (
          <Box>
            <Typography variant="h6" gutterBottom>Mapeo de Bodegas por Tipo de Documento</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo (Item)</TableCell>
                    <TableCell>Código Doc</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Bodega Asociada (Auto-asignar)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {docTypes.map(dt => (
                    <TableRow key={dt.doc_type_code}>
                      <TableCell>{dt.item_type}</TableCell>
                      <TableCell>{dt.doc_type_code}</TableCell>
                      <TableCell>{dt.doc_type_name}</TableCell>
                      <TableCell>
                        <WarehouseAutocomplete
                          initialValue={getWarehouseForDocType(dt.doc_type_code)}
                          onChange={(newCode) => handleWarehouseChange(dt.doc_type_code, newCode)}
                          userId={authState?.user?.id}
                          supabase={supabase}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                  {docTypes.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No hay tipos de documentos creados.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
